import { useState } from "react";

import { trackButtonPressed } from "../../analytics/tracking";
import { PageHeading } from "../../components/dashboard/PageHeading";
import { Badge } from "../../design-system";
import { CheckoutModal } from "../../components/billing/CheckoutModal";
import {
  TokenwiseCheckout,
  TokenwisePaywall,
  useTokenwisePlan,
} from "../../services/stigg";

/* Plan & billing page (OPSS-55). Uses the shared design-system PageHeading + Badge so it
 * reads like every other settings page; the Stigg paywall/checkout are themed to the DS
 * (font, palette, radius, shadows) via STIGG_THEME. Selecting a plan opens Stigg's checkout
 * (Stripe card capture → paid subscription) in a modal. */
export default function BillingPage() {
  const { isLoading, hasSubscription, isEntitled, isTrial, trialDaysLeft } =
    useTokenwisePlan();
  const [checkout, setCheckout] = useState<{
    planId: string;
    billingPeriod?: string;
  } | null>(null);

  const handlePlanSelected = (planId: string, billingPeriod?: string) => {
    // Reuse the generic button-funnel event (no dedicated paywall event yet).
    trackButtonPressed("Upgrade plan", "billing", planId);
    setCheckout({ planId, billingPeriod });
  };

  const statusBadge =
    isLoading || !hasSubscription ? null : isTrial ? (
      <Badge tone="brand">
        Free trial
        {trialDaysLeft !== undefined ? ` · ${trialDaysLeft} days left` : ""}
      </Badge>
    ) : isEntitled ? (
      <Badge tone="success">Active</Badge>
    ) : (
      <Badge tone="error">Trial ended</Badge>
    );

  return (
    <div className="flex flex-col gap-5">
      <PageHeading
        title="Plan & billing"
        subtitle="Manage your Tokenwise subscription and payment method."
        actions={statusBadge}
      />

      <TokenwisePaywall onPlanSelected={handlePlanSelected} />

      <CheckoutModal open={checkout !== null} onClose={() => setCheckout(null)}>
        {checkout && (
          <TokenwiseCheckout
            planId={checkout.planId}
            billingPeriod={checkout.billingPeriod}
            onCompleted={() => setCheckout(null)}
          />
        )}
      </CheckoutModal>
    </div>
  );
}
